import { Injectable } from '@angular/core';
import { DeviceStoreService } from '../device-store/device-store.service';
import { catchError, concatMap, from, map, mapTo, tap } from 'rxjs';
import { RequestOptionsProviderService } from '../request-options-provider/request-options-provider.service';
import { DeviceType, DriverDemographics, DriverManagementService, DriverManagementVerbs, DriverRegisterRequest } from '@cordic/driver-management';
import { mapToVoid } from 'src/app/models/utils/observable-utils';

import packageJson from '../../../../package.json';
import { ErrorType } from '@cordic/request';
import { LoginService } from '../login-service/login-service.service';

@Injectable({
  providedIn: 'root'
})
export class RegisterService {

  constructor( 
    private driverManagementService: DriverManagementService, 
    private deviceStore: DeviceStoreService, 
    private reqOpt: RequestOptionsProviderService,
    private loginService: LoginService) { }

  get userDemographics()
  {
    return this.deviceStore.user.pipe(
      concatMap(user =>
      {
        // Get id from stored JWT if page has been refreshed
        let driverId = (user?.id !== 0 && user?.id !== null) ? user.id : Number(atob((this.reqOpt.jwt as string).split('.')[1]));

        return this.driverManagementService.post(DriverManagementVerbs.DETAILS, { driverUserID: driverId })
          .pipe(map(response => response.demographic));
      }));
  }

  sendDetails(demographic: DriverDemographics)
  {
    return this.driverManagementService.post(DriverManagementVerbs.UPDATE_DETAILS, { demographic }).pipe(
      mapTo(undefined),
      catchError(err =>
      {
        // NOTE: This is a workaround due to base class limitation, i,e expecting pages to display inline errors, etc.
        if (err.errorType === ErrorType.HANDLED)
        {
          return err.translatableMessage.pipe(map(val =>
          {
            throw val;
          }));
        }
        throw err;
      }));
  }

  verify(verifyCode: string)
  {
    return this.driverManagementService.post(DriverManagementVerbs.VERIFY, { verifyCode })
    .pipe(
      tap(response =>
      { 
        this.deviceStore.setSession({
          driverStatus: response.driverStatus,
          verificationRequirement: response.verifyReq,
        });
      }),
      mapToVoid());
  }

  register(phoneNumber?: string, password?: string)
  {
    const isReRegister = (!phoneNumber && !password);

    const version = from(Promise.resolve(packageJson.version));
    return this.deviceStore.deviceId.pipe(
      concatMap(deviceID =>
      {
        return version.pipe(
          concatMap(appVersion =>
          {
            const request = {
              device: DeviceType.BROWSER,
              appVersion,
              deviceID,
              pushID: 'TEST',
              phoneNumber,
              password
            } as DriverRegisterRequest;
            return this.driverManagementService.post(DriverManagementVerbs.REGISTER, request);
          })
        );
      })
    ).pipe(tap({
      next: (response) =>
    {
      if (response?.jwt)
      {
        this.deviceStore.setSession({
          jwt: response.jwt,
          driverStatus: response.driverStatus
        });

        this.reqOpt.jwt = response.jwt;
      }
      if (!isReRegister) {
        this.deviceStore.setUser({
          id: response?.driverUserID,
          name: phoneNumber,
        });
      }
    }}), mapToVoid());
  }

  resendVerificationCode()
  {
    return this.register();
  }

  requestVerifyCode(loginID: string)
  {
    return this.driverManagementService.post(DriverManagementVerbs.FORGOTTEN_PASSWORD, { loginID, phase: 1 })
      .pipe(mapToVoid());
  }

  sendVerifyCode(loginID: string, verifyCode: string)
  {
    return this.driverManagementService.post(DriverManagementVerbs.FORGOTTEN_PASSWORD, { loginID, verifyCode, phase: 2 })
      .pipe(mapToVoid());
  }

  sendNewPassword(loginID: string, verifyCode: string, newPassword: string)
  {
    return this.driverManagementService.post(DriverManagementVerbs.FORGOTTEN_PASSWORD, { loginID, verifyCode, newPassword, phase: 3 })
      .pipe(
        concatMap(() => this.loginService.login(loginID, newPassword,)),
        mapToVoid()
      );
  }
}
